import React from "react";
import * as ReactDOM from "react-dom/client";
import reactToWebcomponent from "react-to-webcomponent";
import PropTypes from "prop-types";
import Widget from "./widget";
import ChannelWidget from "./channelWidget";

/**
 * Widget Component PropTypes
 * @property {string} token - Widget identifier for fetching configuration from API
 * @property {string} apiurl - Optional API URL override for widget data
 * @property {string} viewercountry - ISO country code for geo-based content filtering
 * @property {boolean|string} requireauth - Whether authentication is required to view content
 * @property {string} labels - JSON string for dynamic label filtering based on current video context
 *                             Format: '{"isAndGroup": boolean, "labels": string[]}'
 *                             When provided, overrides widget's static selector configuration
 *                             Example: '{"isAndGroup": false, "labels": ["sports", "football"]}'
 * @property {string} searchtext - Search query text for text-based content search (search page only)
 *                                When provided, overrides all other selector configurations
 *                                Example: 'football highlights'
 * @property {string} orderby - Sort order parameter from search page (e.g., '-start_time', 'start_time')
 *                             Maps to Blaze SDK OrderType in the widget implementation
 * @property {string} refreshkey - Internal counter incremented by VfWidgetElement.refresh() to trigger
 *                                  a live data re-fetch without remounting the element or losing event listeners
 */
Widget.propTypes = {
  token          : PropTypes.string,
  apiurl         : PropTypes.string,
  viewercountry  : PropTypes.string,
  requireauth    : PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  labels         : PropTypes.string,
  searchtext     : PropTypes.string,
  orderby        : PropTypes.string,
  refreshkey     : PropTypes.string,
};

ChannelWidget.propTypes = {
  token : PropTypes.string,
  type  : PropTypes.string
};

/**
 * Custom Web Component for VF Widget
 * Wraps the React Widget component and properly handles attribute changes
 * Compatible with React 18's createRoot API
 */
class VfWidgetElement extends HTMLElement {
  static get observedAttributes() {
    return ['token', 'apiurl', 'viewercountry', 'requireauth', 'labels', 'searchtext', 'orderby', 'refreshkey'];
  }

  constructor() {
    super();
    this._root = null;
    this._isMounted = false;
    this._isUpdating = false;
    this._refreshCounter = 0;
  }

  /**
   * Imperatively trigger a data refresh inside the widget.
   * Increments an internal counter and sets it as the `refreshkey` attribute,
   * which causes attributeChangedCallback → _render() → Widget re-fetches its data.
   * The DOM element and all attached event listeners are preserved.
   */
  refresh() {
    this._refreshCounter += 1;
    this.setAttribute('refreshkey', String(this._refreshCounter));
  }

  /**
   * Called when element is added to the DOM
   * Initialize React root and render the component
   */
  connectedCallback() {
    if (!this._isMounted) {
      try {
        this._root = ReactDOM.createRoot(this);
        this._isMounted = true;
        this._render();
      } catch (error) {
      }
    }
  }

  /**
   * Called when element is removed from the DOM
   * Cleanup React root properly to avoid memory leaks
   */
  disconnectedCallback() {
    if (this._root && this._isMounted) {
      // Use queueMicrotask to defer cleanup and avoid React 18 fiber deletion errors
      // This ensures React has finished all pending updates before unmounting
      queueMicrotask(() => {
        try {
          if (this._root) {
            this._root.unmount();
          }
        } catch (error) {
        } finally {
          this._root = null;
          this._isMounted = false;
          this._isUpdating = false;
        }
      });
    }
  }

  /**
   * Called when an observed attribute changes
   * Triggers a re-render of the React component with new props
   * @param {string} name - The attribute name that changed
   * @param {string} oldValue - Previous value
   * @param {string} newValue - New value
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Only re-render if mounted and value actually changed
    if (this._isMounted && oldValue !== newValue && !this._isUpdating) {
      this._render();
    }
  }

  /**
   * Render or update the React component
   * Maps HTML attributes to React props
   * @private
   */
  _render() {
    if (!this._root || !this._isMounted) {
      return;
    }

    try {
      this._isUpdating = true;
      const props = {
        token: this.getAttribute('token'),
        apiurl: this.getAttribute('apiurl'),
        viewercountry: this.getAttribute('viewercountry'),
        requireauth: this.getAttribute('requireauth'),
        labels: this.getAttribute('labels'),
        searchtext: this.getAttribute('searchtext'),
        orderby: this.getAttribute('orderby'),
        refreshkey: this.getAttribute('refreshkey'),
      };

      // Render the React component with current props
      this._root.render(React.createElement(Widget, props));
    } catch (error) {
      console.error('Render error:', error);
    } finally {
      // Reset updating flag after render is queued
      queueMicrotask(() => {
        this._isUpdating = false;
      });
    }
  }
}

/**
 * Register custom elements
 * vf-widget uses our custom implementation for proper React 18 support
 * vfc-widget still uses the library wrapper (no attribute update needs)
 */
customElements.define("vf-widget", VfWidgetElement);
customElements.define("vfc-widget", reactToWebcomponent(ChannelWidget, React, ReactDOM));
